import React from 'react';
import { Trash2, Plus, XCircle, Clock, CheckCircle } from 'lucide-react';

const InstitutionalStructure = ({ 
  webContent, 
  setWebContent, 
  institutionalTab, 
  handleUpdateWebContent, 
  savingWeb 
}) => {
  return (
    <div className="bg-white rounded-sm shadow-sm border border-slate-200 p-10">
      {/* CONTEXTUAL INTRO BANNERS */}
      {institutionalTab === 'colleges_list' && (
          <div className="bg-[#cc0000] text-white p-8 rounded-sm mb-10 relative overflow-hidden">
              <h4 className="text-2xl font-black uppercase tracking-tighter mb-2 italic">Academic Institutions</h4>
              <p className="text-[10px] font-bold uppercase tracking-widest text-white/70 max-w-xl">
                  Manage the colleges and partner institution links that appear in the "Academic" dropdown menu on the website.
              </p>
          </div>
      )}

      {institutionalTab === 'placements_list' && (
          <div className="bg-slate-900 text-white p-8 rounded-sm mb-10 relative overflow-hidden">
              <h4 className="text-2xl font-black uppercase tracking-tighter mb-2 italic">Placement Portals</h4>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 max-w-xl">
                  Configure corporate recruitment links and placement resources for the "Placements" section.
              </p>
          </div>
      )}

      {institutionalTab === 'research_list' && (
          <div className="bg-[#003366] text-white p-8 rounded-sm mb-10 relative overflow-hidden">
              <h4 className="text-2xl font-black uppercase tracking-tighter mb-2 italic">Research & Development</h4>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-300 max-w-xl">
                  Update research center details and publication links managed under the "Research & Dev" umbrella.
              </p>
          </div>
      )}

      {institutionalTab === 'innovations_list' && (
          <div className="bg-amber-600 text-white p-8 rounded-sm mb-10 relative overflow-hidden">
              <h4 className="text-2xl font-black uppercase tracking-tighter mb-2 italic">Innovations & Startups</h4>
              <p className="text-[10px] font-bold uppercase tracking-widest text-white/80 max-w-xl">
                  Manage the innovation hub links and startup incubation resources for the "Innovations" dropdown.
              </p>
          </div>
      )}

      {institutionalTab === 'journals_list' && (
          <div className="bg-purple-700 text-white p-8 rounded-sm mb-10 relative overflow-hidden">
              <h4 className="text-2xl font-black uppercase tracking-tighter mb-2 italic">Publication & Journals</h4>
              <p className="text-[10px] font-bold uppercase tracking-widest text-white/80 max-w-xl">
                  Manage academic journals, conference papers, and publication categories for the "Research" section.
              </p>
          </div>
      )}
      
      <div className="space-y-12">
        {(webContent[institutionalTab] || []).map((category, catIdx) => (
          <div key={catIdx} className="border border-slate-100 p-6 rounded-sm relative bg-white">
            <button 
              onClick={() => {
                  const newList = JSON.parse(JSON.stringify(webContent[institutionalTab] || []));
                  newList.splice(catIdx, 1);
                  setWebContent({...webContent, [institutionalTab]: newList});
              }}
              className="absolute top-4 right-4 text-red-500 hover:text-red-700 p-2"
              title="Delete Category"
            >
              <Trash2 size={18} />
            </button>
            
            <div className="mb-6">
              <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Category Name</label>
              <input 
                type="text" 
                placeholder="Enter Category Name..."
                className="w-full border-b border-slate-200 py-2 outline-none focus:border-[#cc0000] font-bold text-lg"
                value={category.category || ''}
                onChange={e => {
                  const newList = JSON.parse(JSON.stringify(webContent[institutionalTab] || []));
                  newList[catIdx].category = e.target.value;
                  setWebContent({...webContent, [institutionalTab]: newList});
                }}
              />
            </div>
            
            <div className="space-y-8">
              <label className="block text-[10px] font-black uppercase text-slate-500">Subcategories & Links</label>
              {(category.subCategories || []).map((subCat, subIdx) => (
                <div key={subIdx} className="border border-slate-200 p-5 rounded-sm bg-slate-50/30 relative group/sub">
                  <button 
                    onClick={() => {
                      const newList = JSON.parse(JSON.stringify(webContent[institutionalTab] || []));
                      newList[catIdx].subCategories.splice(subIdx, 1);
                      setWebContent({...webContent, [institutionalTab]: newList});
                    }}
                    className="absolute top-4 right-4 text-red-400 hover:text-red-600 p-1"
                    title="Delete Subcategory"
                  >
                    <XCircle size={16} />
                  </button>

                  <div className="mb-4 pr-10">
                    <label className="block text-[8px] font-bold uppercase text-slate-400 mb-1">Subcategory Name (e.g. B.ED, BA LLB)</label>
                    <input 
                      type="text" 
                      placeholder="Enter Subcategory Name..."
                      className="w-full border-b border-slate-200 bg-transparent py-1 outline-none focus:border-[#cc0000] text-sm font-black uppercase"
                      value={subCat.name || ''}
                      onChange={e => {
                        const newList = JSON.parse(JSON.stringify(webContent[institutionalTab] || []));
                        newList[catIdx].subCategories[subIdx].name = e.target.value;
                        setWebContent({...webContent, [institutionalTab]: newList});
                      }}
                    />
                  </div>

                  <div className="space-y-3 pl-4 border-l-2 border-slate-100">
                    {(subCat.items || []).map((item, itemIdx) => (
                      <div key={itemIdx} className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-white p-3 border border-slate-100 rounded-sm relative group/item shadow-sm">
                        <button 
                          onClick={() => {
                            const newList = JSON.parse(JSON.stringify(webContent[institutionalTab] || []));
                            newList[catIdx].subCategories[subIdx].items.splice(itemIdx, 1);
                            setWebContent({...webContent, [institutionalTab]: newList});
                          }}
                          className="absolute -top-2 -right-2 bg-white text-red-500 p-1 rounded-full shadow-md opacity-0 group-hover/item:opacity-100 transition-opacity border border-slate-100"
                        >
                          <XCircle size={14} />
                        </button>
                        <div>
                          <label className="block text-[7px] font-bold uppercase text-slate-400 mb-1">Item Name</label>
                          <input 
                            type="text" 
                            placeholder="College or Portal Name"
                            className="w-full border-b border-slate-100 py-1 outline-none focus:border-[#cc0000] text-xs font-bold"
                            value={item.name || ''}
                            onChange={e => {
                              const newList = JSON.parse(JSON.stringify(webContent[institutionalTab] || []));
                              newList[catIdx].subCategories[subIdx].items[itemIdx].name = e.target.value;
                              setWebContent({...webContent, [institutionalTab]: newList});
                            }}
                          />
                        </div>
                        <div>
                          <label className="block text-[7px] font-bold uppercase text-slate-400 mb-1">URL</label>
                          <input 
                            type="text" 
                            placeholder="Website URL"
                            className="w-full border-b border-slate-100 py-1 outline-none focus:border-[#cc0000] text-xs text-blue-600 font-semibold"
                            value={item.url || ''}
                            onChange={e => {
                              const newList = JSON.parse(JSON.stringify(webContent[institutionalTab] || []));
                              newList[catIdx].subCategories[subIdx].items[itemIdx].url = e.target.value;
                              setWebContent({...webContent, [institutionalTab]: newList});
                            }}
                          />
                        </div>
                      </div>
                    ))}
                    <button 
                      onClick={() => {
                        const newList = JSON.parse(JSON.stringify(webContent[institutionalTab] || []));
                        if (!newList[catIdx].subCategories[subIdx].items) newList[catIdx].subCategories[subIdx].items = [];
                        newList[catIdx].subCategories[subIdx].items.push({ name: '', url: '' });
                        setWebContent({...webContent, [institutionalTab]: newList});
                      }}
                      className="flex items-center text-[9px] font-black uppercase text-slate-400 hover:text-[#cc0000]"
                    >
                      <Plus size={12} className="mr-1" /> Add Item to {subCat.name || 'Subcategory'}
                    </button>
                  </div>
                </div>
              ))}
              
              <button 
                onClick={() => {
                  const newList = JSON.parse(JSON.stringify(webContent[institutionalTab] || []));
                  if (!newList[catIdx].subCategories) newList[catIdx].subCategories = [];
                  newList[catIdx].subCategories.push({ name: '', items: [{ name: '', url: '' }] });
                  setWebContent({...webContent, [institutionalTab]: newList});
                }}
                className="flex items-center justify-center w-full border border-dashed border-slate-300 py-4 text-[10px] font-black uppercase text-slate-500 hover:border-[#cc0000] hover:text-[#cc0000] bg-white transition-all"
              >
                <Plus size={14} className="mr-2" /> Add New Subcategory
              </button>
            </div>
          </div>
        ))}
        
        <button 
          onClick={() => {
            const newList = JSON.parse(JSON.stringify(webContent[institutionalTab] || []));
            newList.push({ 
              category: '', 
              subCategories: [{ name: '', items: [{ name: '', url: '' }] }] 
            });
            setWebContent({...webContent, [institutionalTab]: newList});
          }}
          className="w-full border-2 border-dashed border-slate-200 py-6 text-slate-400 font-bold uppercase text-xs hover:border-[#cc0000] hover:text-[#cc0000] transition-all flex items-center justify-center bg-slate-50/50"
        >
          <Plus size={20} className="mr-2" /> Create New {institutionalTab.split('_')[0]} Category
        </button>

      </div>
      
      <div className="mt-12 pt-10 border-t border-slate-100 flex items-center justify-between">
          <div>
              <p className="text-xs font-bold text-slate-400 uppercase">Status: {savingWeb ? 'Saving...' : 'Ready to save'}</p>
              <p className="text-[10px] text-slate-400">Updates will be published across the entire navigation structure.</p>
          </div>
          <button 
            onClick={handleUpdateWebContent}
            disabled={savingWeb}
            className="bg-[#cc0000] text-white px-12 py-5 font-black uppercase text-xs tracking-[4px] shadow-2xl hover:bg-slate-900 transition-all disabled:opacity-50 flex items-center"
          >
            {savingWeb ? (
                <>
                  <Clock className="w-4 h-4 mr-2 animate-spin" /> Publishing...
                </>
            ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" /> Publish entire structure
                </>
            )}
          </button>
      </div>
    </div>
  );
};

export default InstitutionalStructure;
